import { useState, useMemo, type JSX } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Plus, Package } from 'lucide-react';
import clsx from 'clsx';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import { useParamStore } from '../../hooks/useParamStore';
import { MapWarningChip } from '../ui/MapWarningChip';
import { ProvenanceTooltip } from '../ui/ProvenanceTooltip';
import type { ProvenanceMap } from '../ui/ProvenanceIndicator';
import type {
  CalcProfitabilitySalePriceSource,
  CalcProfitabilityVelocitySource,
} from '../../lib/engineFlags';

// ── Formatting ───────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(1)}%`;
}

// ── Marketplace labels ───────────────────────────────────────────────────────

// Day 75.5 P2A — covers the ~60ms cache-miss window before useSettingsConfig
// populates activePaths. Values mirror Day 66 P2.1 fpc.short_label seed shape
// (FBA / FBM / eBay / WMT-Self / WFS / Shopify; Day 75 added 'DROP' for
// dropship). Sol C9 lock 2026-05-15: short_label form for compact Cost
// Breakdown header + best-channel labels — matches grid-density use.
// Day 60b *_FALLBACK pattern (BACKLOG L242 reference).
const MP_LABEL_FALLBACK: Record<string, string> = {
  amazon_fba: 'FBA',
  amazon_fbm: 'FBM',
  ebay: 'eBay',
  walmart_self: 'WMT-Self',
  walmart_wfs: 'WFS',
  shopify: 'Shopify',
};

// ── Row shape read from channels map ─────────────────────────────────────────

interface ChannelRow {
  no_price_data?: boolean | null;
  is_profitable?: boolean | null;
  meets_min_roi?: boolean | null;
  sale_price?: number | null;
  total_cost_per_unit?: number | null;
  marketplace_fees?: number | null;
  returns_cost?: number | null;
  profit_per_unit?: number | null;
  roi_pct?: number | null;
  effective_floor?: number | null;
  profit_per_day_safety?: number | null;
  profit_per_day_velocity?: number | null;
  roi_per_day_safety?: number | null;
  roi_per_day_velocity?: number | null;
  // Day 76 P5.1 Phase 6 — MAP UI surface (ADR 41 Shape B). UI gates chip
  // render on === true only (NULL + FALSE both render no chip).
  below_map?: boolean | null;
  // Day 84.8 P3 — engine-side per-row source attribution flags per
  // src/lib/engineFlags.ts (parallel to BuyingSession ChannelProfitRow
  // Phase 1 extension). Optional per F9 additive contract.
  sale_price_source?: CalcProfitabilitySalePriceSource | null;
  velocity_source?: CalcProfitabilityVelocitySource | null;
}

// ── Status color ─────────────────────────────────────────────────────────────

function statusColor(row: ChannelRow | undefined | null): string {
  if (!row || row.no_price_data) return 'text-ink-muted';
  if (row.meets_min_roi) return 'text-success';
  if (row.is_profitable) return 'text-warning';
  return 'text-danger';
}

// ── DetailPanel ──────────────────────────────────────────────────────────────

interface DetailPanelProduct {
  id: string;
  isg_sku?: string | null;
  title?: string | null;
  brand?: string | null;
  // U10 A1 — junction-assembled upstream (the BuyingSession fetch: primary
  // product_distributors row → cost_usd; slice-B drop family).
  cost_usd?: number | null;
  asin?: string | null;
  weight_lbs?: number | null;
  algopix_recommendation?: string | null;
  amazon_demand_rating?: string | null;
  image_url?: string | null;
  // Day 64 F55: dedicated listing URL columns (products.amazon/ebay/walmart_listing_url).
  // Source of truth for listing links; algopix_data jsonb retained for image_url fallback only.
  amazon_listing_url?: string | null;
  ebay_listing_url?: string | null;
  walmart_listing_url?: string | null;
  algopix_data?: Record<string, unknown> | null;
  // Day 76 P5.1 Phase 6 — MAP UI surface (ADR 41 Shape B). map_price lives
  // on products.map_price; brands embed from PostgREST nested SELECT
  // carries name + enforces_map for the warning chip tooltip attribution.
  map_price?: number | null;
  brands?: { name?: string | null; enforces_map?: boolean | null } | null;
}

interface AddToPOPayload {
  productId: string;
  isgSku: string | null | undefined;
  title: string | null | undefined;
  brand: string | null | undefined;
  unitCost: number;
  allocations: Record<string, string>;
}

interface DetailPanelProps {
  open: boolean;
  onToggle: () => void;
  product: DetailPanelProduct | null;
  channels: Record<string, ChannelRow | undefined>;
  safetyCap: boolean;
  onAddToPO: (payload: AddToPOPayload) => void;
}

type DecisionCols = {
  profitPerUnit?: number | null;
  roiPct?: number | null;
  profitPerDay?: number | null;
  roiPerDay?: number | null;
};

export default function DetailPanel({
  open,
  onToggle,
  product,
  channels,
  safetyCap,
  onAddToPO,
}: DetailPanelProps): JSX.Element {
  const { activePaths, allPaths } = useSettingsConfig();
  const paramStore = useParamStore();
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  // Day 84.8 P3 — staleThresholdDays for ProvenanceTooltip wraps per Lock Q5
  // uniform 90-day default; per-domain divergence deferred to Day 91a P7.5.
  const staleThresholdDays = useMemo(() => {
    const raw = Number(paramStore.resolveKey('manual_override_stale_days')?.param.value);
    return Number.isFinite(raw) ? raw : 90;
  }, [paramStore]);

  // Day 75.5 P2A — derive mpLabel from fpc.short_label per Sol C9 lock
  // (grid-density form for Cost Breakdown header + best-channel labels).
  // Falls back to MP_LABEL_FALLBACK during the ~60ms cache miss window
  // before useSettingsConfig populates activePaths. Mirrors BuyingSession.tsx
  // mpLabel useMemo pattern with short_label substitution. Day 60b *_FALLBACK
  // pattern (BACKLOG L242).
  const mpLabel = useMemo<Record<string, string>>(() => {
    if (activePaths.length === 0) return MP_LABEL_FALLBACK;
    return Object.fromEntries(
      activePaths.map((p) => [p.path_key, p.short_label || p.display_name])
    );
  }, [activePaths]);

  // Day 76 P5.1 Phase 7 — F4 deactivated affordance (Audit #1 F4 / Day 72c
  // F7). Channel Comparison table iterates allLabels so deactivated paths
  // surface with muted (opacity-50) styling + tooltip; operator sees full
  // channel picture including paths that aren't currently emitting rows.
  // Quantity Allocation below keeps using active-only mpLabel — you don't
  // allocate to a deactivated path. Per Sol Phase 7 lock 2026-05-17
  // option (2) DetailPanel-only; grid extension deferred (Day 77+
  // follow-up if partner UAT signals).
  const allLabels = useMemo<Record<string, string>>(() => {
    if (allPaths.length === 0) return MP_LABEL_FALLBACK;
    return Object.fromEntries(allPaths.map((p) => [p.path_key, p.short_label || p.display_name]));
  }, [allPaths]);
  const activePathKeysSet = useMemo<Set<string>>(
    () => new Set(activePaths.map((p) => p.path_key)),
    [activePaths]
  );

  // Compute key metrics — best ROI%, Profit/Unit, Profit/Day across active channels
  const keyMetrics = useMemo(() => {
    if (!channels || Object.keys(channels).length === 0) return null;
    let bestRoi: { value: number | null; channel: string | null } = { value: null, channel: null };
    let bestProfit: { value: number | null; channel: string | null } = {
      value: null,
      channel: null,
    };
    let bestProfitDay: { value: number | null; channel: string | null } = {
      value: null,
      channel: null,
    };
    for (const [mp, row] of Object.entries(channels)) {
      if (!row || row.no_price_data) continue;
      if (!mpLabel[mp]) continue;
      const roi = Number(row.roi_pct);
      const ppu = Number(row.profit_per_unit);
      const ppd = Number(safetyCap ? row.profit_per_day_safety : row.profit_per_day_velocity) || 0;
      if (bestRoi.value === null || roi > bestRoi.value)
        bestRoi = { value: roi, channel: mpLabel[mp] };
      if (bestProfit.value === null || ppu > bestProfit.value)
        bestProfit = { value: ppu, channel: mpLabel[mp] };
      if (bestProfitDay.value === null || ppd > bestProfitDay.value)
        bestProfitDay = { value: ppd, channel: mpLabel[mp] };
    }
    return { bestRoi, bestProfit, bestProfitDay };
  }, [channels, safetyCap, mpLabel]);

  // Collapsed bar
  if (!open) {
    return (
      <button
        onClick={onToggle}
        className="w-6 flex-shrink-0 bg-surface-1 border-l border-border flex items-center justify-center
                   hover:bg-surface-3 transition-colors cursor-pointer"
        title="Show product detail"
        aria-label="Show product detail"
      >
        <ChevronLeft size={12} className="text-ink-muted" />
      </button>
    );
  }

  if (!product) {
    return (
      <div className="w-80 flex-shrink-0 bg-surface-1 border-l border-border flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
          <span className="text-xs font-semibold text-ink-bright">Product Detail</span>
          <button
            onClick={onToggle}
            className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-4"
            aria-label="Hide product detail"
          >
            <ChevronRight size={12} />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-ink-muted">
            <Package size={24} className="mx-auto mb-2 opacity-40" />
            <p className="text-xs">Select a product to view details</p>
          </div>
        </div>
      </div>
    );
  }

  const algopixData = (product.algopix_data ?? {}) as Record<string, unknown>;
  const imageUrl =
    product.image_url || (typeof algopixData.image_url === 'string' ? algopixData.image_url : null);

  // Build listing links — Day 64 F55: prefer dedicated columns over algopix_data jsonb.
  // Amazon cascades dedicated column → ASIN-construction fallback (ASIN is more reliably
  // populated than amazon_listing_url until Algopix data flow fills the new columns).
  // eBay + Walmart: dedicated column only (no equivalent fallback identifier).
  const links: { label: string; url: string }[] = [];
  if (product.amazon_listing_url) {
    links.push({ label: 'Amazon', url: product.amazon_listing_url });
  } else if (product.asin) {
    links.push({ label: 'Amazon', url: `https://www.amazon.com/dp/${product.asin}` });
  }
  if (product.ebay_listing_url) {
    links.push({ label: 'eBay', url: product.ebay_listing_url });
  }
  if (product.walmart_listing_url) {
    links.push({ label: 'Walmart', url: product.walmart_listing_url });
  }

  // Decision columns for a profitability row
  function decisionCols(row: ChannelRow | undefined): DecisionCols {
    if (!row) return {};
    return {
      profitPerUnit: row.profit_per_unit,
      roiPct: row.roi_pct,
      profitPerDay: safetyCap ? row.profit_per_day_safety : row.profit_per_day_velocity,
      roiPerDay: safetyCap ? row.roi_per_day_safety : row.roi_per_day_velocity,
    };
  }

  // Selected (click-locked) > hovered > first available path for cost breakdown
  const activePath: string =
    selectedPath && channels[selectedPath]
      ? selectedPath
      : hoveredPath && channels[hoveredPath]
        ? hoveredPath
        : (Object.keys(channels)[0] ?? '');
  const activeRow = channels[activePath];

  // Allocation totals
  const totalUnits = Object.values(allocations).reduce((s, v) => s + (parseInt(v) || 0), 0);
  const totalCost = Object.entries(allocations).reduce((s, [mp, qty]) => {
    const row = channels[mp];
    return s + (parseInt(qty) || 0) * Number(row?.total_cost_per_unit || product.cost_usd || 0);
  }, 0);
  const totalProfit = Object.entries(allocations).reduce((s, [mp, qty]) => {
    const row = channels[mp];
    return s + (parseInt(qty) || 0) * Number(row?.profit_per_unit || 0);
  }, 0);

  function handleAddToPO(): void {
    if (totalUnits === 0 || !product) return;
    onAddToPO({
      productId: product.id,
      isgSku: product.isg_sku,
      title: product.title,
      brand: product.brand,
      unitCost: Number(product.cost_usd || 0),
      allocations: { ...allocations },
    });
    setAllocations({});
  }

  return (
    <div className="w-80 flex-shrink-0 bg-surface-1 border-l border-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
        <span className="text-xs font-semibold text-ink-bright">Product Detail</span>
        <button
          onClick={onToggle}
          className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-4"
          aria-label="Hide product detail"
        >
          <ChevronRight size={12} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pr-2.5">
        {/* Product header */}
        <div className="px-3 py-3 border-b border-border-subtle">
          <div className="flex gap-3">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="w-14 h-14 rounded border border-border object-contain bg-white flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded border border-border bg-surface-3 flex items-center justify-center flex-shrink-0">
                <Package size={16} className="text-ink-muted" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] font-mono text-accent">{product.isg_sku}</p>
              <p
                className="text-xs font-medium text-ink-bright truncate"
                title={product.title ?? undefined}
              >
                {product.title || '—'}
              </p>
              <p className="text-2xs text-ink-muted">{product.brand || '—'}</p>
            </div>
          </div>

          {/* Key metrics */}
          <div className="flex gap-3 mt-2 text-2xs">
            <div>
              <span className="text-ink-muted">Cost:</span>{' '}
              <span className="font-mono text-ink-bright">{fmt$(product.cost_usd)}</span>
            </div>
            {product.weight_lbs && (
              <div>
                <span className="text-ink-muted">Weight:</span>{' '}
                <span className="text-ink-secondary">{product.weight_lbs} lbs</span>
              </div>
            )}
          </div>

          {/* Algopix recommendation */}
          {(product.algopix_recommendation || product.amazon_demand_rating) && (
            <div className="flex gap-3 mt-1.5 text-2xs">
              {product.algopix_recommendation && (
                <div>
                  <span className="text-ink-muted">Rec:</span>{' '}
                  <span className="text-ink-secondary">{product.algopix_recommendation}</span>
                </div>
              )}
              {product.amazon_demand_rating && (
                <div>
                  <span className="text-ink-muted">Demand:</span>{' '}
                  <span className="text-ink-secondary">{product.amazon_demand_rating}</span>
                </div>
              )}
            </div>
          )}

          {/* Links */}
          {links.length > 0 && (
            <div className="flex gap-2 mt-2">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-2xs text-accent hover:text-accent-hover flex items-center gap-0.5"
                >
                  {l.label} <ExternalLink size={8} />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Key metrics summary */}
        {keyMetrics && (
          <div className="border-b border-border-subtle">
            <div className="px-3 py-1.5 text-2xs font-semibold text-ink-muted uppercase tracking-wider bg-surface-2">
              Key Metrics
            </div>
            <div className="px-3 py-2 space-y-1 text-2xs">
              <div className="flex justify-between">
                <span className="text-ink-muted">Best ROI%</span>
                <span>
                  <span
                    className={clsx(
                      'font-mono font-medium',
                      keyMetrics.bestRoi.value !== null && keyMetrics.bestRoi.value > 0
                        ? 'text-success'
                        : keyMetrics.bestRoi.value !== null
                          ? 'text-danger'
                          : 'text-ink-muted'
                    )}
                  >
                    {fmtPct(keyMetrics.bestRoi.value)}
                  </span>
                  {keyMetrics.bestRoi.channel && (
                    <span className="text-ink-muted ml-1">via {keyMetrics.bestRoi.channel}</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Best Profit/Unit</span>
                <span>
                  <span
                    className={clsx(
                      'font-mono font-medium',
                      keyMetrics.bestProfit.value !== null && keyMetrics.bestProfit.value > 0
                        ? 'text-success'
                        : keyMetrics.bestProfit.value !== null
                          ? 'text-danger'
                          : 'text-ink-muted'
                    )}
                  >
                    {fmt$(keyMetrics.bestProfit.value)}
                  </span>
                  {keyMetrics.bestProfit.channel && (
                    <span className="text-ink-muted ml-1">via {keyMetrics.bestProfit.channel}</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Best Profit/Day</span>
                <span>
                  <span
                    className={clsx(
                      'font-mono font-medium',
                      keyMetrics.bestProfitDay.value !== null && keyMetrics.bestProfitDay.value > 0
                        ? 'text-success'
                        : keyMetrics.bestProfitDay.value !== null
                          ? 'text-danger'
                          : 'text-ink-muted'
                    )}
                  >
                    {fmt$(keyMetrics.bestProfitDay.value)}
                  </span>
                  {keyMetrics.bestProfitDay.channel && (
                    <span className="text-ink-muted ml-1">
                      via {keyMetrics.bestProfitDay.channel}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Channel comparison — all paths; deactivated rendered muted per
            Day 76 P5.1 Phase 7 F4 affordance (Audit #1 F4 / Day 72c F7) */}
        <div className="border-b border-border-subtle">
          <div className="px-3 py-1.5 text-2xs font-semibold text-ink-muted uppercase tracking-wider bg-surface-2">
            Channel Comparison
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-2xs">
              <thead>
                <tr className="text-ink-muted border-b border-border-subtle">
                  <th className="px-2 py-1 text-left font-medium">Path</th>
                  <th className="px-2 py-1 text-left font-medium">Price</th>
                  <th className="px-2 py-1 text-left font-medium">Profit</th>
                  <th className="px-2 py-1 text-left font-medium">ROI</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(allLabels).map(([mp, label]) => {
                  const row = channels[mp];
                  const cols = decisionCols(row);
                  const isActive = activePathKeysSet.has(mp);
                  return (
                    <tr
                      key={mp}
                      className={clsx(
                        'border-b border-border-subtle hover:bg-surface-3 cursor-pointer transition-colors',
                        selectedPath === mp && 'bg-accent-muted',
                        hoveredPath === mp && !selectedPath && 'bg-surface-3',
                        !isActive && 'opacity-50'
                      )}
                      onMouseEnter={() => setHoveredPath(mp)}
                      onMouseLeave={() => setHoveredPath(null)}
                      onClick={() => setSelectedPath((prev) => (prev === mp ? null : mp))}
                      title={!isActive ? `${label} is currently deactivated` : undefined}
                    >
                      <td className={clsx('px-2 py-1.5 font-medium', statusColor(row))}>{label}</td>
                      <td className="px-2 py-1.5 text-left font-mono text-ink-secondary">
                        {/* Day 84.8 P3 — wrap with ProvenanceTooltip inline
                            mode when sale_price_source present (F9 engine
                            attribution; synthetic envelope per IA-7). */}
                        {row?.sale_price_source ? (
                          <ProvenanceTooltip
                            provenance={
                              {
                                sale_price: { source: row.sale_price_source },
                              } as ProvenanceMap
                            }
                            fieldKey="sale_price"
                            staleThresholdDays={staleThresholdDays}
                            mode="inline"
                          >
                            <span className="inline-flex items-center gap-1">
                              {fmt$(row?.sale_price)}
                              {row?.below_map === true && (
                                <MapWarningChip
                                  mapPrice={product?.map_price ?? null}
                                  brandName={product?.brands?.name ?? null}
                                />
                              )}
                            </span>
                          </ProvenanceTooltip>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            {fmt$(row?.sale_price)}
                            {row?.below_map === true && (
                              <MapWarningChip
                                mapPrice={product?.map_price ?? null}
                                brandName={product?.brands?.name ?? null}
                              />
                            )}
                          </span>
                        )}
                      </td>
                      <td className={clsx('px-2 py-1.5 text-left font-mono', statusColor(row))}>
                        {fmt$(cols.profitPerUnit)}
                      </td>
                      <td className={clsx('px-2 py-1.5 text-left font-mono', statusColor(row))}>
                        {fmtPct(cols.roiPct)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cost breakdown for active/hovered path */}
        {activeRow && (
          <div className="border-b border-border-subtle">
            <div className="px-3 py-1.5 text-2xs font-semibold text-ink-muted uppercase tracking-wider bg-surface-2 flex items-center gap-1">
              <span>Cost Breakdown — {mpLabel[activePath]}</span>
              {activeRow?.below_map === true && (
                <MapWarningChip
                  mapPrice={product?.map_price ?? null}
                  brandName={product?.brands?.name ?? null}
                />
              )}
            </div>
            <div className="px-3 py-2 space-y-1 text-2xs">
              <div className="flex justify-between">
                <span className="text-ink-muted">Product Cost</span>
                <span className="font-mono text-ink-secondary">{fmt$(product.cost_usd)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Total Cost/Unit</span>
                <span className="font-mono text-ink-secondary">
                  {fmt$(activeRow.total_cost_per_unit)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Marketplace Fees</span>
                <span className="font-mono text-ink-secondary">
                  {fmt$(activeRow.marketplace_fees)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Returns Cost</span>
                <span className="font-mono text-ink-secondary">{fmt$(activeRow.returns_cost)}</span>
              </div>
              <div className="flex justify-between border-t border-border-subtle pt-1 mt-1">
                <span className="text-ink-bright font-medium">Profit/Unit</span>
                <span className={clsx('font-mono font-medium', statusColor(activeRow))}>
                  {fmt$(activeRow.profit_per_unit)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Floor Price</span>
                <span className="font-mono text-ink-secondary">
                  {fmt$(activeRow.effective_floor)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Quantity allocation */}
        <div className="border-b border-border-subtle">
          <div className="px-3 py-1.5 text-2xs font-semibold text-ink-muted uppercase tracking-wider bg-surface-2">
            Quantity Allocation
          </div>
          <div className="px-3 py-2 space-y-1.5">
            {Object.entries(mpLabel).map(([mp, label]) => {
              const row = channels[mp];
              if (!row || row.no_price_data) return null;
              return (
                <div key={mp} className="flex items-center gap-2 text-2xs">
                  <span className={clsx('w-14 truncate', statusColor(row))}>{label}</span>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        const cur = parseInt(allocations[mp] ?? '') || 0;
                        if (cur > 0) setAllocations((prev) => ({ ...prev, [mp]: String(cur - 1) }));
                      }}
                      className="w-5 h-5 flex items-center justify-center rounded bg-surface-3 border border-border
                                 hover:bg-surface-4 text-ink-secondary text-xs cursor-pointer select-none"
                      aria-label={`Decrease ${label} allocation`}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={allocations[mp] || ''}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '');
                        setAllocations((prev) => ({ ...prev, [mp]: v }));
                      }}
                      placeholder="0"
                      aria-label={`${label} allocation`}
                      className="w-8 h-5 text-center text-xs font-mono bg-surface-3 border border-border rounded
                                 text-ink-bright outline-none focus:border-accent"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const cur = parseInt(allocations[mp] ?? '') || 0;
                        setAllocations((prev) => ({ ...prev, [mp]: String(cur + 1) }));
                      }}
                      className="w-5 h-5 flex items-center justify-center rounded bg-surface-3 border border-border
                                 hover:bg-surface-4 text-ink-secondary text-xs cursor-pointer select-none"
                      aria-label={`Increase ${label} allocation`}
                    >
                      +
                    </button>
                  </div>
                  <span className="text-ink-muted flex-1 text-right font-mono">
                    {allocations[mp]
                      ? fmt$(Number(allocations[mp]) * Number(row.total_cost_per_unit || 0))
                      : ''}
                  </span>
                </div>
              );
            })}

            {/* Totals */}
            <div className="border-t border-border-subtle pt-1.5 mt-1.5 space-y-0.5 text-2xs">
              <div className="flex justify-between">
                <span className="text-ink-muted">Total Units</span>
                <span className="font-mono text-ink-bright">{totalUnits}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Total Cost</span>
                <span className="font-mono text-ink-bright">{fmt$(totalCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Est. Profit</span>
                <span
                  className={clsx('font-mono', totalProfit >= 0 ? 'text-success' : 'text-danger')}
                >
                  {fmt$(totalProfit)}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAddToPO();
              }}
              disabled={totalUnits === 0}
              className="w-full arbi-btn-primary text-xs py-1.5 mt-2 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={12} /> {totalUnits > 0 ? `Add ${totalUnits} to PO` : 'Enter qty to add'}
            </button>
          </div>
        </div>

        {/* Buying history */}
        <div>
          <div className="px-3 py-1.5 text-2xs font-semibold text-ink-muted uppercase tracking-wider bg-surface-2">
            Buying History
          </div>
          <div className="px-3 py-4 text-center">
            <p className="text-2xs text-ink-muted">No history yet</p>
          </div>
        </div>
      </div>
    </div>
  );
}
